import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('suios_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors (but not for login endpoint)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
      localStorage.removeItem('suios_token');
      localStorage.removeItem('suios_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
};

// User management APIs
export const usersAPI = {
  list: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.patch(`/users/${id}`, data),
  get: (id) => api.get(`/users/${id}`),
  getSalespeople: () => api.get('/users/salespeople'),
};

// Dashboard APIs
export const dashboardAPI = {
  getData: () => api.get('/dashboard'),
  sidebarCounts: () => api.get('/sidebar-counts'),
};

// Global Search
export const searchAPI = {
  search: (q) => api.get('/search', { params: { q } }),
};

// Fabric APIs
export const fabricsAPI = {
  list: (params) => api.get('/fabrics', { params }),
  get: (id) => api.get(`/fabrics/${id}`),
  create: (data) => api.post('/fabrics', data),
  update: (id, data) => api.patch(`/fabrics/${id}`, data),
  getFilterOptions: () => api.get('/fabrics/filter-options'),
  uploadSwatch: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/fabrics/${id}/swatch`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  setSwatchUrl: (id, url) => api.post(`/fabrics/${id}/swatch-url`, { url }),
  migrateSwatch: () => api.post('/fabrics/migrate-swatch-urls'),
  bulkImagesPreview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/fabrics/bulk-update-images/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  bulkImagesConfirm: (rows) => api.post('/fabrics/bulk-update-images/confirm', { rows }),
  importPreview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/fabrics/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importConfirm: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/fabrics/import/confirm', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listAliases: (fabricId) => api.get(`/fabrics/${fabricId}/aliases`),
  createAlias: (fabricId, data) => api.post(`/fabrics/${fabricId}/aliases`, data),
  deleteAlias: (aliasId) => api.delete(`/aliases/${aliasId}`),
};

// Client APIs
export const clientsAPI = {
  list: (params) => api.get('/clients', { params }),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.patch(`/clients/${id}`, data),
  search: (q) => api.get('/clients/search', { params: { q } }),
  getFilterOptions: () => api.get('/clients/filter-options'),
  getReferrals: (id) => api.get(`/clients/${id}/referrals`),
  listPhotos: (id) => api.get(`/clients/${id}/photos`),
  uploadPhoto: (id, file, photoType, notes) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('photo_type', photoType);
    formData.append('photo_notes', notes || '');
    return api.post(`/clients/${id}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePhoto: (photoId) => api.delete(`/client-photos/${photoId}`),
  listNotes: (id) => api.get(`/clients/${id}/notes`),
  createNote: (id, noteText) => api.post(`/clients/${id}/notes`, { note_text: noteText }),
  softDelete: (id) => api.post(`/clients/${id}/delete`),
  restore: (id) => api.post(`/clients/${id}/restore`),
  listArchived: () => api.get('/clients-archived'),
  getAuditLog: (id) => api.get(`/clients/${id}/audit-log`),
  bulkArchive: (clientIds) => api.post('/clients-bulk-archive', { client_ids: clientIds }),
  profileSummary: (id) => api.get(`/clients/${id}/profile-summary`),
};

// Measurement APIs
export const measurementsAPI = {
  getFields: (garmentContext, includeInstructions = false) =>
    api.get(`/measurements/fields/${garmentContext}`, { params: { include_instructions: includeInstructions } }),
  getValidationRanges: () => api.get('/measurements/validation-ranges'),
  list: (clientId) => api.get(`/clients/${clientId}/measurements`),
  get: (setId) => api.get(`/measurements/${setId}`),
  create: (clientId, data) => api.post(`/clients/${clientId}/measurements`, data),
  update: (setId, params) => api.patch(`/measurements/${setId}`, null, { params }),
  editValues: (setId, data) => api.put(`/measurements/${setId}/values`, data),
  compare: (clientId) => api.get(`/clients/${clientId}/measurements/compare`),
  delete: (setId) => api.delete(`/measurements/${setId}`),
};

// Worker APIs
export const workersAPI = {
  list: (params) => api.get('/workers', { params }),
  get: (workerId) => api.get(`/workers/${workerId}`),
  create: (data) => api.post('/workers', data),
  update: (workerId, data) => api.patch(`/workers/${workerId}`, data),
  delete: (workerId) => api.delete(`/workers/${workerId}`),
  listCapabilities: (workerId) => api.get(`/workers/${workerId}/capabilities`),
  addCapability: (workerId, data) => api.post(`/workers/${workerId}/capabilities`, data),
  updateCapability: (capId, data) => api.patch(`/capabilities/${capId}`, data),
  deleteCapability: (capId) => api.delete(`/capabilities/${capId}`),
  getAvailability: (workerId, year, month) => api.get(`/workers/${workerId}/availability`, { params: { year, month } }),
  setAvailability: (workerId, data) => api.put(`/workers/${workerId}/availability`, data),
  deleteAvailability: (workerId, date) => api.delete(`/workers/${workerId}/availability/${date}`),
  getGarmentTypes: () => api.get('/workers/meta/garment-types'),
};

// Trims APIs
export const trimsAPI = {
  list: (params) => api.get('/trims', { params }),
  get: (trimId) => api.get(`/trims/${trimId}`),
  create: (data) => api.post('/trims', data),
  update: (trimId, data) => api.patch(`/trims/${trimId}`, data),
  delete: (trimId) => api.delete(`/trims/${trimId}`),
  adjustStock: (trimId, data) => api.post(`/trims/${trimId}/adjust-stock`, data),
  uploadImage: (trimId, formData) => api.post(`/trims/${trimId}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Orders APIs
export const ordersAPI = {
  meta: () => api.get('/orders/meta'),
  customisationSpec: () => api.get('/orders/customisation-spec'),
  updateHouseDefaults: (data) => api.put('/orders/house-defaults', data),
  calculateConsumption: (data) => api.post('/orders/calculate-consumption', data),
  consumptionRules: () => api.get('/consumption-rules'),
  updateConsumptionRules: (data) => api.put('/consumption-rules', data),
  list: (params) => api.get('/orders', { params }),
  get: (orderId) => api.get(`/orders/${orderId}`),
  create: (data) => api.post('/orders', data),
  update: (orderId, data) => api.patch(`/orders/${orderId}`, data),
  updateStatus: (orderId, data) => api.post(`/orders/${orderId}/status`, data),
  deliver: (orderId, data) => api.post(`/orders/${orderId}/deliver`, data),
  workflowContext: (orderId) => api.get(`/orders/${orderId}/workflow-context`),
  delete: (orderId) => api.delete(`/orders/${orderId}`),
  clientOrders: (clientId) => api.get(`/clients/${clientId}/orders`),
  updateItemStatus: (itemId, data) => api.post(`/order-items/${itemId}/status`, data),
};

// Purchase Order APIs
export const poAPI = {
  pending: () => api.get('/purchase-orders/pending'),
  list: (params) => api.get('/purchase-orders', { params }),
  get: (poId) => api.get(`/purchase-orders/${poId}`),
  create: (data) => api.post('/purchase-orders', data),
  updateStatus: (poId, data) => api.post(`/purchase-orders/${poId}/status`, data),
  receiveLineItem: (lineId, data) => api.post(`/po-line-items/${lineId}/receive`, data),
  whatsappText: (poId) => api.get(`/purchase-orders/${poId}/whatsapp-text`),
};

// Payment APIs
export const paymentsAPI = {
  record: (data) => api.post('/payments', data),
  listForOrder: (orderId) => api.get(`/orders/${orderId}/payments`),
  updateSchedule: (orderId, data) => api.put(`/orders/${orderId}/payment-schedule`, data),
  overdueOrders: () => api.get('/payments/overdue-orders'),
};

// Finance APIs
export const financesAPI = {
  outstanding: () => api.get('/finances/outstanding'),
  recentPayments: (limit = 20) => api.get('/finances/recent-payments', { params: { limit } }),
  revenueThisMonth: () => api.get('/finances/revenue-this-month'),
  expectedIncoming: () => api.get('/finances/expected-incoming'),
  monthlyRevenue: () => api.get('/finances/monthly-revenue'),
};

// Costing APIs
export const costingAPI = {
  get: (orderId) => api.get(`/costing/${orderId}`),
  recalculate: (orderId) => api.post(`/costing/${orderId}/recalculate`),
  update: (orderId, data) => api.patch(`/costing/${orderId}`, data),
  profitability: () => api.get('/costing/profitability/analytics'),
};

// Production APIs
export const productionAPI = {
  tasks: (params) => api.get('/production/tasks', { params }),
  summary: () => api.get('/production/summary'),
  pipeline: () => api.get('/production/pipeline'),
  updateStatus: (taskId, data) => api.post(`/production/tasks/${taskId}/status`, data),
  reassign: (taskId, data) => api.post(`/production/tasks/${taskId}/reassign`, data),
  outsourceSend: (taskId, data) => api.post(`/production/tasks/${taskId}/outsource-send`, data),
  outsourceReceive: (taskId, data) => api.post(`/production/tasks/${taskId}/outsource-receive`, data),
  fabricReceived: (orderId) => api.post(`/production/fabric-received/${orderId}`),
  reorder: (taskIds) => api.post('/production/reorder', { task_ids: taskIds }),
};

// QC APIs
export const qcAPI = {
  getTemplate: (garmentType, qcType) => api.get('/qc/template', { params: { garment_type: garmentType, qc_type: qcType } }),
  listTemplates: () => api.get('/qc/templates'),
  updateTemplate: (templateId, data) => api.put(`/qc/template/${templateId}`, data),
  createInspection: (data) => api.post('/qc/inspections', data),
  listInspections: (params) => api.get('/qc/inspections', { params }),
  getInspection: (id) => api.get(`/qc/inspections/${id}`),
  analytics: () => api.get('/qc/analytics'),
};

// Vendor Invoice APIs
export const vendorInvoicesAPI = {
  upload: (formData) => api.post('/vendor-invoices/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  list: (params) => api.get('/vendor-invoices', { params }),
  get: (id) => api.get(`/vendor-invoices/${id}`),
  extract: (id) => api.post(`/vendor-invoices/${id}/extract`),
  saveLineItems: (id, data) => api.post(`/vendor-invoices/${id}/line-items`, data),
  runMatching: (id) => api.post(`/vendor-invoices/${id}/match`),
  confirmMatch: (invoiceId, lineId, data) => api.post(`/vendor-invoices/${invoiceId}/line-items/${lineId}/confirm`, data),
  changeMatch: (invoiceId, lineId, data) => api.post(`/vendor-invoices/${invoiceId}/line-items/${lineId}/change-match`, data),
  ignoreLine: (invoiceId, lineId) => api.post(`/vendor-invoices/${invoiceId}/line-items/${lineId}/ignore`),
  disputeLine: (invoiceId, lineId) => api.post(`/vendor-invoices/${invoiceId}/line-items/${lineId}/dispute`),
  searchFabrics: (q) => api.get('/vendor-invoices/search-fabrics', { params: { q } }),
  delete: (id) => api.delete(`/vendor-invoices/${id}`),
};

// Vendor Score APIs
export const vendorScoresAPI = {
  list: () => api.get('/vendor-scores'),
  get: (vendorName) => api.get(`/vendor-scores/${encodeURIComponent(vendorName)}`),
  batch: (vendorNames) => api.post('/vendor-scores/batch', { vendor_names: vendorNames }),
  recalculate: (vendorName) => api.post(`/vendor-scores/${encodeURIComponent(vendorName)}/recalculate`),
  recalculateAll: () => api.post('/vendor-scores/recalculate-all'),
  updateRating: (vendorName, data) => api.put(`/vendor-scores/${encodeURIComponent(vendorName)}/rating`, data),
  badge: (vendorName) => api.get(`/vendor-scores/badge/${encodeURIComponent(vendorName)}`),
};

// Feedback APIs
export const feedbackAPI = {
  forOrder: (orderId) => api.get(`/feedback/order/${orderId}`),
  forClient: (clientId) => api.get(`/feedback/client/${clientId}`),
};

// Alteration APIs
export const alterationsAPI = {
  meta: () => api.get('/alterations/meta'),
  timeMatrix: () => api.get('/alterations/time-matrix'),
  updateTimeMatrix: (data) => api.put('/alterations/time-matrix', data),
  create: (data) => api.post('/alterations', data),
  list: (params) => api.get('/alterations', { params }),
  updateStatus: (id, data) => api.post(`/alterations/${id}/status`, data),
  approveNoAlterations: (data) => api.post('/alterations/approve-no-alterations', data),
};

// Standalone Alteration APIs
export const standaloneAlterationsAPI = {
  meta: () => api.get('/standalone-alterations/meta'),
  charges: () => api.get('/standalone-alterations/charges'),
  updateCharges: (data) => api.put('/standalone-alterations/charges', data),
  addCharge: (data) => api.post('/standalone-alterations/charges', data),
  list: (params) => api.get('/standalone-alterations', { params }),
  get: (id) => api.get(`/standalone-alterations/${id}`),
  create: (data) => api.post('/standalone-alterations', data),
  updateStatus: (id, data) => api.post(`/standalone-alterations/${id}/status`, data),
  recordPayment: (id, data) => api.post(`/standalone-alterations/${id}/payment`, data),
  revenueSummary: () => api.get('/standalone-alterations/revenue-summary'),
  productionTasks: () => api.get('/standalone-alterations/production-tasks'),
};

// Alteration Analytics APIs
export const alterationAnalyticsAPI = {
  patterns: (months = 6) => api.get('/alteration-analytics/patterns', { params: { months } }),
  workerSummary: (month) => api.get('/alteration-analytics/worker-summary', { params: { month } }),
  workerDetails: (workerId, month) => api.get(`/alteration-analytics/worker-details/${workerId}`, { params: { month } }),
};

// Admin Settings APIs
export const settingsAPI = {
  getAll: () => api.get('/settings'),
  get: (key) => api.get(`/settings/${key}`),
  update: (key, value) => api.put(`/settings/${key}`, { value }),
  getMakingCharges: () => api.get('/settings-making-charges'),
  updateMakingCharges: (data) => api.put('/settings-making-charges', data),
  getExpressSettings: () => api.get('/settings-express'),
  updateExpressSettings: (data) => api.put('/settings-express', data),
  getReferralTiers: () => api.get('/referral-tiers'),
  updateReferralTiers: (data) => api.put('/referral-tiers', data),
};

// Referral Rewards APIs
export const referralRewardsAPI = {
  list: () => api.get('/referral-rewards'),
  getUnredeemed: (clientId) => api.get(`/referral-rewards/unredeemed/${clientId}`),
  redeem: (rewardId, orderId) => api.post(`/referral-rewards/${rewardId}/redeem`, null, { params: { order_id: orderId } }),
};

// Notification APIs
export const notificationsAPI = {
  listTemplates: () => api.get('/notifications/templates'),
  getTemplate: (id) => api.get(`/notifications/templates/${id}`),
  updateTemplate: (id, data) => api.put(`/notifications/templates/${id}`, data),
  createTemplate: (data) => api.post('/notifications/templates', data),
  render: (data) => api.post('/notifications/render', data),
  send: (data) => api.post('/notifications/send', data),
  updateLogStatus: (logId, status) => api.put(`/notifications/log/${logId}/status`, { status }),
  getLog: (params) => api.get('/notifications/log', { params }),
  getPending: (orderId) => api.get(`/notifications/pending/${orderId}`),
};

// Appointment APIs
export const appointmentsAPI = {
  list: (params) => api.get('/appointments', { params }),
  get: (id) => api.get(`/appointments/${id}`),
  create: (data) => api.post('/appointments', data),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  slots: (date, params) => api.get(`/appointments/slots/${date}`, { params }),
  today: () => api.get('/appointments/today'),
  getSettings: () => api.get('/appointment-settings'),
  updateSettings: (data) => api.put('/appointment-settings', data),
  popupEvents: () => api.get('/popup-events'),
  createPopupEvent: (data) => api.post('/popup-events', data),
  updatePopupEvent: (id, data) => api.put(`/popup-events/${id}`, data),
  deletePopupEvent: (id) => api.delete(`/popup-events/${id}`),
};

// Public Booking APIs (no auth)
export const publicBookingAPI = {
  slots: (date, params) => axios.get(`${BACKEND_URL}/api/public/booking/slots/${date}`, { params }),
  settings: () => axios.get(`${BACKEND_URL}/api/public/booking/settings`),
  eventInfo: (slug) => axios.get(`${BACKEND_URL}/api/public/booking/event/${slug}`),
  book: (data) => axios.post(`${BACKEND_URL}/api/public/booking`, data),
};

// Cash Voucher / Petty Cash APIs
export const cashVouchersAPI = {
  list: (params) => api.get('/cash-vouchers', { params }),
  get: (id) => api.get(`/cash-vouchers/${id}`),
  create: (data) => api.post('/cash-vouchers', data),
  update: (id, data) => api.patch(`/cash-vouchers/${id}`, data),
  delete: (id) => api.delete(`/cash-vouchers/${id}`),
  uploadReceipt: (id, formData) => api.post(`/cash-vouchers/${id}/receipt`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  review: (id, data) => api.post(`/cash-vouchers/${id}/review`, data),
  monthlySummary: (params) => api.get('/cash-vouchers/summary/monthly', { params }),
  pendingCount: () => api.get('/cash-vouchers/pending-count'),
  thisMonthTotal: () => api.get('/cash-vouchers/this-month-total'),
  bankDepositsThisMonth: () => api.get('/cash-vouchers/bank-deposits-this-month'),
};

// Style Advisor APIs
export const styleAdvisorAPI = {
  // Style Rules (knowledge base)
  listRules: (params) => api.get('/style-rules', { params }),
  createRule: (data) => api.post('/style-rules', data),
  updateRule: (id, data) => api.patch(`/style-rules/${id}`, data),
  deleteRule: (id) => api.delete(`/style-rules/${id}`),
  seedRules: () => api.post('/style-rules/seed'),
  
  // Style Recommendations
  generateRecommendation: (data) => api.post('/style-recommendations/generate', data),
  listRecommendations: (params) => api.get('/style-recommendations', { params }),
  getRecommendation: (id) => api.get(`/style-recommendations/${id}`),
  acceptRecommendation: (id, data) => api.post(`/style-recommendations/${id}/accept`, data),
  updateRecommendationStatus: (id, status, notes) => 
    api.post(`/style-recommendations/${id}/status`, null, { params: { status, notes } }),
  itemAction: (id, itemIndex, action, modifications) =>
    api.post(`/style-recommendations/${id}/item-action`, modifications, { params: { item_index: itemIndex, action } }),
  
  // Photo Analysis
  analyzePhoto: (imageUrl) => api.post('/style-advisor/analyze-photo', null, { params: { image_url: imageUrl } }),
  
  // Client Insights
  getClientInsights: (clientId) => api.get(`/style-advisor/client-insights/${clientId}`),
  
  // Utilities
  getEnums: () => api.get('/style-advisor/enums'),
  getStats: () => api.get('/style-advisor/stats'),
};

// ============= TRY-ON & LOOKBOOK APIs =============
export const tryonAPI = {
  // Configuration
  getConfig: () => api.get('/tryon/config'),
  updateConfig: (config) => api.put('/tryon/config', config),
  
  // Image Generation
  generate: (data) => api.post('/tryon/generate', data),
  listImages: (params) => api.get('/tryon/images', { params }),
  getImage: (id) => api.get(`/tryon/images/${id}`),
  rateImage: (id, rating) => api.post(`/tryon/images/${id}/rate`, { quality_rating: rating }),
  regenerateImage: (id) => api.post(`/tryon/images/${id}/regenerate`),
  saveToGallery: (imageId, clientId) => api.post(`/tryon/images/${imageId}/save-to-gallery`, { client_id: clientId }),
  saveQuickTryToClient: (data) => api.post('/tryon/quick-try/save-to-client', data),
  
  // AI Archive
  listArchive: (params) => api.get('/tryon/archive', { params }),
  saveToArchive: (data) => api.post('/tryon/archive', data),
  deleteArchive: (id) => api.delete(`/tryon/archive/${id}`),
  assignArchiveToClient: (archiveId, clientId) => api.post(`/tryon/archive/${archiveId}/assign`, { client_id: clientId }),
  
  // Photo Assessment
  assessPhoto: (photoId) => api.post(`/client-photos/${photoId}/assess-tryon`),
  setPhotoSuitable: (photoId, suitable) => api.put(`/client-photos/${photoId}/tryon-suitable`, null, { params: { suitable } }),
};

export const lookbookAPI = {
  // Campaigns
  listCampaigns: (params) => api.get('/lookbook/campaigns', { params }),
  getCampaign: (id) => api.get(`/lookbook/campaigns/${id}`),
  createCampaign: (data) => api.post('/lookbook/campaigns', data),
  startGeneration: (id) => api.post(`/lookbook/campaigns/${id}/generate`),
  
  // Cards
  listCards: (params) => api.get('/lookbook/cards', { params }),
  createCard: (tryonImageId, title, description, whyForYou, price) => 
    api.post('/lookbook/cards', null, { params: { tryon_image_id: tryonImageId, look_title: title, look_description: description, why_for_you: whyForYou, price_estimate: price } }),
  updateCard: (id, data) => api.put(`/lookbook/cards/${id}`, data),
  sendCard: (id, via = 'whatsapp') => api.post(`/lookbook/cards/${id}/send`, null, { params: { via } }),
  markEnquiry: (id) => api.post(`/lookbook/cards/${id}/enquiry`),
};

// ============= ENGAGEMENT APIs =============
export const engagementAPI = {
  // Engagements
  list: (params) => api.get('/engagements', { params }),
  getToday: () => api.get('/engagements/today'),
  send: (id) => api.post(`/engagements/${id}/send`),
  skip: (id, reason) => api.post(`/engagements/${id}/skip`, null, { params: { reason } }),
  update: (id, data) => api.put(`/engagements/${id}`, data),
  cancel: (id) => api.delete(`/engagements/${id}`),
  scheduleForOrder: (orderId) => api.post(`/engagements/schedule-for-order/${orderId}`),
  scheduleBirthday: (clientId) => api.post(`/engagements/schedule-birthday/${clientId}`),
  scheduleAnniversary: (clientId) => api.post(`/engagements/schedule-anniversary/${clientId}`),
  
  // Settings
  getSettings: () => api.get('/engagement-settings'),
  updateSettings: (updates) => api.put('/engagement-settings', updates),
  
  // Care Rules
  getCareRules: (garmentType) => api.get('/garment-care-rules', { params: { garment_type: garmentType } }),
  updateCareRule: (id, data) => api.put(`/garment-care-rules/${id}`, data),
};

// ============= INTERNATIONAL OPERATIONS APIs =============
export const internationalAPI = {
  // Exchange Rates
  getExchangeRates: () => api.get('/exchange-rates'),
  updateExchangeRates: (data) => api.put('/exchange-rates', data),
  convertCurrency: (amount, from, to = 'INR') => api.post('/convert-currency', null, { params: { amount, from_currency: from, to_currency: to } }),
  getCurrencyForCountry: (country) => api.get(`/currency-for-country/${country}`),
  
  // Shipping
  getOrderShipping: (orderId) => api.get(`/orders/${orderId}/shipping`),
  updateOrderShipping: (orderId, data) => api.put(`/orders/${orderId}/shipping`, data),
  markOrderShipped: (orderId, trackingNumber, provider) => api.post(`/orders/${orderId}/shipping/mark-shipped`, null, { params: { tracking_number: trackingNumber, shipping_provider: provider } }),
  
  // Pop-up Events
  listEvents: (params) => api.get('/events', { params }),
  getEvent: (id) => api.get(`/events/${id}`),
  getEventBySlug: (slug) => api.get(`/events/by-slug/${slug}`),
  createEvent: (data) => api.post('/events', data),
  updateEvent: (id, data) => api.put(`/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/events/${id}`),
  getEventSlots: (eventId, date) => api.get(`/events/${eventId}/slots`, { params: { date } }),
  inviteClients: (eventId) => api.post(`/events/${eventId}/invite-clients`),
  getEventReport: (eventId) => api.get(`/events/${eventId}/report`),
  
  // Consultation
  getConsultationSettings: () => api.get('/consultation-settings'),
  updateConsultationSettings: (data) => api.put('/consultation-settings', data),
  
  // Swatch Requests
  listSwatchRequests: (status) => api.get('/swatch-requests', { params: { status } }),
  createSwatchRequest: (data) => api.post('/swatch-requests', data),
  updateSwatchRequest: (id, status, trackingNumber) => api.put(`/swatch-requests/${id}`, null, { params: { status, tracking_number: trackingNumber } }),
  
  // Timezone
  getTimezoneForCountry: (country) => api.get(`/timezone-for-country/${country}`),
  updateClientTimezone: (clientId, timezone) => api.put(`/clients/${clientId}/timezone`, { timezone }),
  
  // Meta
  getCountryMeta: () => api.get('/meta/countries'),
};

// ============= ANALYTICS DASHBOARD APIs =============
export const analyticsAPI = {
  // Revenue & Financial
  getRevenueSummary: (rangeKey, startDate, endDate) => api.get('/analytics/revenue/summary', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate } }),
  getRevenueTrend: (rangeKey, granularity, startDate, endDate) => api.get('/analytics/revenue/trend', { params: { range_key: rangeKey, granularity, start_date: startDate, end_date: endDate } }),
  getRevenueByGarmentType: (rangeKey, startDate, endDate) => api.get('/analytics/revenue/breakdown/garment-type', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate } }),
  getRevenueByFabricTier: (rangeKey, startDate, endDate) => api.get('/analytics/revenue/breakdown/fabric-tier', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate } }),
  getRevenueByClientSource: (rangeKey, startDate, endDate) => api.get('/analytics/revenue/breakdown/client-source', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate } }),
  getRevenueByStylist: (rangeKey, startDate, endDate) => api.get('/analytics/revenue/breakdown/stylist', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate } }),
  getCashFlowForecast: () => api.get('/analytics/revenue/cash-flow-forecast'),
  getVendorSpend: (rangeKey, startDate, endDate) => api.get('/analytics/revenue/vendor-spend', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate } }),
  
  // Client Analytics
  getClientSummary: (rangeKey, startDate, endDate) => api.get('/analytics/clients/summary', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate } }),
  getClientCLVRanking: (limit) => api.get('/analytics/clients/clv-ranking', { params: { limit } }),
  getClientSegmentation: () => api.get('/analytics/clients/segmentation'),
  getClientLocationDistribution: () => api.get('/analytics/clients/location-distribution'),
  getReferralNetwork: () => api.get('/analytics/clients/referral-network'),
  
  // Production Analytics
  getProductionSummary: (rangeKey, startDate, endDate) => api.get('/analytics/production/summary', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate } }),
  getWorkerProductivity: (rangeKey, startDate, endDate) => api.get('/analytics/production/worker-productivity', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate } }),
  getAvgProductionTime: (rangeKey, startDate, endDate) => api.get('/analytics/production/avg-production-time', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate } }),
  getAlterationStats: (rangeKey, startDate, endDate) => api.get('/analytics/production/alteration-stats', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate } }),
  getCapacityUtilisation: () => api.get('/analytics/production/capacity-utilisation'),
  
  // Fabric & Product Analytics
  getBestSellingFabrics: (rangeKey, startDate, endDate, limit) => api.get('/analytics/fabric/best-selling', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate, limit } }),
  getFabricTierMix: (rangeKey, startDate, endDate) => api.get('/analytics/fabric/tier-mix', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate } }),
  getSlowMovingFabrics: (days) => api.get('/analytics/fabric/slow-moving', { params: { days } }),
  getGarmentTypePopularity: (rangeKey, startDate, endDate) => api.get('/analytics/fabric/garment-type-popularity', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate } }),
  getSeasonalTrends: () => api.get('/analytics/fabric/seasonal-trends'),
  
  // Engagement Analytics
  getEngagementSummary: (rangeKey, startDate, endDate) => api.get('/analytics/engagement/summary', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate } }),
  getNotificationBreakdown: (rangeKey, startDate, endDate) => api.get('/analytics/engagement/notification-breakdown', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate } }),
  getReferralProgramStats: (rangeKey, startDate, endDate) => api.get('/analytics/engagement/referral-program', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate } }),
  getLookbookCampaignStats: (rangeKey, startDate, endDate) => api.get('/analytics/engagement/lookbook-campaigns', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate } }),
  getPostDeliveryStats: (rangeKey, startDate, endDate) => api.get('/analytics/engagement/post-delivery', { params: { range_key: rangeKey, start_date: startDate, end_date: endDate } }),
};

// Loyalty Program APIs
export const loyaltyAPI = {
  // Program
  getProgram: (clientId) => api.get(`/loyalty/program/${clientId}`),
  getTransactions: (clientId, limit = 20) => api.get(`/loyalty/transactions/${clientId}`, { params: { limit } }),
  earnPoints: (data) => api.post('/loyalty/earn', data),
  awardBonus: (clientId, points, bonusType, description, orderId = null) => 
    api.post('/loyalty/bonus', null, { params: { client_id: clientId, points, bonus_type: bonusType, description, order_id: orderId } }),
  redeemReward: (data) => api.post('/loyalty/redeem', data),
  
  // Rewards Catalogue
  getRewards: (activeOnly = true) => api.get('/loyalty/rewards', { params: { active_only: activeOnly } }),
  getReward: (id) => api.get(`/loyalty/rewards/${id}`),
  createReward: (data) => api.post('/loyalty/rewards', data),
  updateReward: (id, data) => api.patch(`/loyalty/rewards/${id}`, data),
  deleteReward: (id) => api.delete(`/loyalty/rewards/${id}`),
  
  // Client specific
  getEligibleRewards: (clientId) => api.get(`/loyalty/eligible-rewards/${clientId}`),
  
  // Apply reward as discount on order
  applyToOrder: (data) => api.post('/loyalty/apply-to-order', data),
  
  // Birthday bonus
  awardBirthdayBonus: (clientId) => api.post(`/loyalty/birthday-bonus/${clientId}`),
  
  // Monthly statement
  getMonthlyStatement: (clientId, params) => api.get(`/loyalty/monthly-statement/${clientId}`, { params }),
  sendMonthlyStatements: () => api.post('/loyalty/send-monthly-statements'),
  
  // Settings & Stats
  getSettings: () => api.get('/loyalty/settings'),
  updateSettings: (data) => api.put('/loyalty/settings', data),
  getStats: () => api.get('/loyalty/stats'),
  getTopClients: (limit = 5) => api.get('/loyalty/top-clients', { params: { limit } }),
};

export const configuratorIllustrationsAPI = {
  reference: () => api.get('/configurator-illustrations/reference'),
  list: (garmentType) => api.get('/configurator-illustrations', { params: garmentType ? { garment_type: garmentType } : {} }),
  lookup: (garmentType) => api.get('/configurator-illustrations/lookup', { params: { garment_type: garmentType } }),
  upload: (garmentType, parameter, optionValue, file) => {
    const formData = new FormData();
    formData.append('garment_type', garmentType);
    formData.append('parameter', parameter);
    formData.append('option_value', optionValue);
    formData.append('file', file);
    return api.post('/configurator-illustrations/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  setUrl: (data) => api.post('/configurator-illustrations/set-url', data),
  remove: (id) => api.delete(`/configurator-illustrations/${id}`),
  bulkUpload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/configurator-illustrations/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};


export default api;
